import React, { useCallback, useEffect, useState } from "react";
import { IndexMasjid, loadMasjidIndex, nearestMasjids, searchMasjids } from "./masjidIndex";
import { isTv } from "./nativeApp";

interface MasjidPickerProps {
  // Tapping a row picks it (setup, adding a favourite)...
  onPick?: (masjid: IndexMasjid) => void;
  // ...or each row gets its own buttons (the search sheet).
  renderActions?: (masjid: IndexMasjid) => React.ReactNode;
  // Masjids already chosen show as "Added" and can't be picked again.
  added?: number[];
  autoFocus?: boolean;
}

type Row = { masjid: IndexMasjid; miles?: number };

// TVs have no location, so no Near me.
const onTv = isTv();

const formatMiles = (miles: number) =>
  `${miles < 10 ? miles.toFixed(1) : Math.round(miles)} miles`;

const MasjidPicker: React.FC<MasjidPickerProps> = ({
  onPick,
  renderActions,
  added = [],
  autoFocus,
}) => {
  const [index, setIndex] = useState<IndexMasjid[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState("");
  const [near, setNear] = useState<Row[] | null>(null);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState("");

  const load = useCallback(() => {
    setFailed(false);
    loadMasjidIndex()
      .then(setIndex)
      .catch(() => setFailed(true));
  }, []);

  useEffect(load, [load]);

  const findNearMe = () => {
    if (!index || !navigator.geolocation) {
      setLocationError("Location isn't available on this device. Search by name or town instead.");
      return;
    }
    setLocating(true);
    setLocationError("");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false);
        setQuery("");
        setNear(nearestMasjids(index, position.coords.latitude, position.coords.longitude));
      },
      () => {
        setLocating(false);
        setLocationError("Couldn't find your location. Search by name or town instead.");
      },
      { timeout: 15000, maximumAge: 10 * 60 * 1000 },
    );
  };

  const rows: Row[] = query.trim()
    ? searchMasjids(index ?? [], query).map((masjid) => ({ masjid }))
    : (near ?? []);

  return (
    <div className="hs-picker">
      <div className="hs-picker-bar">
        <input
          className="hs-input"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Masjid name or town"
          aria-label="Masjid name or town"
          autoFocus={autoFocus}
        />
        {!onTv && (
          <button className="hs-button" onClick={findNearMe} disabled={!index || locating}>
            {locating ? "Finding…" : "Near me"}
          </button>
        )}
      </div>

      {locationError && <p className="hs-note">{locationError}</p>}

      {failed ? (
        <div className="hs-empty">
          <p>Can't load the list of masjids. Check the internet connection.</p>
          <button className="hs-button" onClick={load}>
            Try again
          </button>
        </div>
      ) : !index ? (
        <p className="hs-empty">Loading masjids…</p>
      ) : rows.length === 0 ? (
        <p className="hs-empty">
          {query.trim()
            ? `No masjid matches “${query.trim()}”. Try the town name.`
            : onTv
              ? "Type a masjid's name or town."
              : "Type a masjid's name or town, or tap Near me."}
        </p>
      ) : (
        <ul className="hs-rows">
          {rows.map(({ masjid, miles }) => {
            const isAdded = added.includes(masjid.id);
            const details = (
              <>
                <span className="hs-row-name">
                  {masjid.name}
                  {masjid.audio && <span className="hs-pill">Live audio</span>}
                  {isAdded && <span className="hs-pill hs-pill-muted">Added</span>}
                </span>
                <span className="hs-row-town">
                  {[masjid.town, miles !== undefined ? formatMiles(miles) : ""]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </>
            );
            return (
              <li key={masjid.id} className="hs-row">
                {onPick ? (
                  <button
                    className="hs-row-info"
                    onClick={() => onPick(masjid)}
                    disabled={isAdded}
                  >
                    {details}
                  </button>
                ) : (
                  <div className="hs-row-info">{details}</div>
                )}
                {renderActions && <div className="hs-row-actions">{renderActions(masjid)}</div>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default MasjidPicker;
